import React, { useState, useEffect } from 'react';
import { TextField, Box, Typography, InputAdornment } from '@material-ui/core';
import '../ActiveFeatures/features.css';
import { CustomModal } from '../../CustomModal/CustomModal';
import { OverlayLoading } from '../../OverlayLoading/OverlayLoading';
import FeaturesListSection from '../ActiveFeatures/FeaturesListSection';

interface FeatureWithLimit {
  id: number;
  name: string;
  max_limit?: number;
}

interface Feature {
  id: number;
  name: string;
  config: any;
  isvisible: boolean;
}
interface AddPlanModalProps {
  show: boolean;
  onClose?: { (): void };
  allFeatures: Feature[];
  companyName: string;
  companyId: number; // Add this
  planName: string; // Add this
  requestData: any;
  existingQuotation?: any;
  handlePlanSubmit: (params: any) => Promise<void>;
  isSubmitting: boolean;
}

const AddPlanModal: React.FC<AddPlanModalProps> = ({
  show,
  onClose,
  allFeatures,
  companyName,
  companyId, // Add this
  planName, // Add this
  handlePlanSubmit,
  isSubmitting,
}) => {
  const [selectedFeatures, setSelectedFeatures] = useState<number[]>([]);
  const [featureLimits, setFeatureLimits] = useState<{ [key: number]: number }>(
    {}
  );
  const [planAmount, setPlanAmount] = useState<string>('');
  const [planAmountError, setPlanAmountError] = useState<string>('');

  const FEATURES_WITH_LIMITS = [9, 17, 18];

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      // Default select features 17 and 18 (USERS and ACCOUNTS)
      setSelectedFeatures([17, 18]);
      setFeatureLimits({});
      setPlanAmount('');
      setPlanAmountError('');
    }
  }, [show]);

  const handleFeatureToggle = (featureId: number) => {
    if (isSubmitting) return;

    // Prevent deselecting features 17 and 18
    if ([17, 18].includes(featureId)) {
      return;
    }

    setSelectedFeatures((prev) => {
      if (prev.includes(featureId)) {
        setFeatureLimits((prevLimits) => {
          const newLimits = { ...prevLimits };
          delete newLimits[featureId];
          return newLimits;
        });
        return prev.filter((id) => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });
  };

  const handleLimitChange = (featureId: number, limit: number) => {
    setFeatureLimits((prev) => ({
      ...prev,
      [featureId]: limit,
    }));
  };

  const handlePlanAmountChange = (value: string) => {
    setPlanAmount(value);

    // Validate plan amount
    if (!value.trim()) {
      setPlanAmountError('Plan amount is required');
    } else if (isNaN(parseFloat(value)) || parseFloat(value) <= 0) {
      setPlanAmountError('Plan amount must be greater than 0');
    } else {
      setPlanAmountError('');
    }
  };

  // Update handleSubmit to ensure default features are always included
  const handleSubmit = async () => {
    // Validate plan amount before submit
    if (!planAmount.trim()) {
      setPlanAmountError('Plan amount is required');
      return;
    }

    const amount = parseFloat(planAmount);
    if (isNaN(amount) || amount <= 0) {
      setPlanAmountError('Plan amount must be greater than 0');
      return;
    }

    // Ensure features 17 and 18 are always included
    const featuresWithLimits = selectedFeatures.map((featureId) => {
      const limit = featureLimits[featureId];
      return {
        feature_id: featureId,
        max_limit: limit !== undefined && limit > 0 ? limit : null,
      };
    });

    const payload = {
      CompanyId: companyId,
      PlanName: planName,
      PlanPrice: parseFloat(planAmount),
      PlanFeatures: featuresWithLimits,
    };

    try {
      await handlePlanSubmit(payload);
    } catch (error) {
      console.error('Error submitting plan:', error);
    }
  };

  const selectedCount = selectedFeatures.length;
  const totalVisible = allFeatures.filter((f) => f.isvisible).length;

  const isSaveDisabled =
    selectedFeatures.length === 0 ||
    !planAmount.trim() ||
    !!planAmountError ||
    isSubmitting;

  const emptyFeatures: FeatureWithLimit[] = [];

  return (
    <CustomModal
      show={show}
      onClose={onClose}
      headerMsg={`Add Plan - ${companyName}`}
      isSubmitBtnDisabled={isSaveDisabled}
      submitFunc={handleSubmit}
      buttonMsg={isSubmitting ? 'Saving...' : 'Add Plan'}
    >
      <Box className="featuresContainer">
        {isSubmitting && <OverlayLoading />}

        <Box className="featuresHeaderSection">
          <Box className="featuresCountInfo">
            <Typography variant="body2" className="featuresCountText">
              {selectedCount} of {totalVisible} features selected
            </Typography>
          </Box>

          {/* Plan Amount Input */}
          <Box>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Monthly Plan Amount"
              placeholder="Enter plan amount"
              value={planAmount}
              onChange={(e) => handlePlanAmountChange(e.target.value)}
              disabled={isSubmitting}
              error={!!planAmountError}
              helperText={planAmountError}
              type="number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      $
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                min: 0,
                step: 0.01,
                style: { fontSize: '14px' },
              }}
            />
          </Box>

          <Box
            style={{
              padding: '12px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
            }}
          >
            <Typography
              variant="caption"
              style={{ color: '#1565c0', fontSize: '12px' }}
            >
              <strong>Note:</strong> Select the features to include in this plan
              and specify the monthly amount to charge.
            </Typography>
          </Box>
        </Box>

        <FeaturesListSection
          filteredFeatures={allFeatures}
          selectedFeatures={selectedFeatures}
          featureLimits={featureLimits}
          activeFeatures={emptyFeatures}
          isSubmitting={isSubmitting}
          onFeatureToggle={handleFeatureToggle}
          onLimitChange={handleLimitChange}
          featuresWithLimits={FEATURES_WITH_LIMITS}
        />
      </Box>
    </CustomModal>
  );
};

export default AddPlanModal;
